import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Divider,
  Stack,
  Avatar,
  Chip
} from '@mui/material';
import { 
  Settings as SettingsIcon, 
  Person as PersonIcon,
  Lock as LockIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useEasyCheck } from '../../../context/EasyCheckContext';
import { EasyCheckButton } from '../../common/Controls';
import ChangePasswordForm from '../Auth/ChangePasswordForm';

const SettingsContainer = styled(Box)(({ theme }) => ({
  maxWidth: '800px',
  margin: '0 auto',
  padding: theme.spacing(3),
  height: 'calc(100vh - 64px)',
  overflowY: 'auto',
}));

const SettingsCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const SettingsPage = () => {
  const { user } = useEasyCheck();
  const navigate = useNavigate();

  const getUserName = () => {
    if (!user) return 'User';
    return user.name || user.firstName || `${user.firstName} ${user.lastName}`.trim() || user.username || 'User';
  };

  const getUserEmail = () => {
    if (!user) return '';
    return user.email || user.username || '';
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const isAdmin = user?.role === 'Admin';

  return (
    <SettingsContainer>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
        Account Settings
      </Typography>

      {/* User Profile Section */}
      <SettingsCard>
        <CardContent sx={{ p: 3 }}>
          <SectionHeader>
            <PersonIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Profile Information
            </Typography>
          </SectionHeader>

          <Stack direction="row" spacing={3} alignItems="center">
            <Avatar
              sx={{ 
                width: 80, 
                height: 80,
                bgcolor: 'primary.main',
                fontSize: '1.5rem',
                fontWeight: 600
              }}
            >
              {getInitials(getUserName())}
            </Avatar>
            
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {getUserName()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {getUserEmail()}
              </Typography>
              
              <Stack direction="row" spacing={1}>
                <Chip 
                  label={user?.role || 'User'} 
                  color="primary" 
                  variant="outlined" 
                  size="small"
                />
                {user?.companyName && (
                  <Chip 
                    label={user.companyName} 
                    color="secondary" 
                    variant="outlined" 
                    size="small"
                  />
                )}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </SettingsCard>

      {/* User Management Section - Only for Admin users */}
      {isAdmin && (
        <SettingsCard>
          <CardContent sx={{ p: 3 }}>
            <SectionHeader>
              <PeopleIcon sx={{ color: 'info.main', fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                User Management
              </Typography>
            </SectionHeader>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Manage users in your company, add new team members, and control access levels.
            </Typography>

            <EasyCheckButton
              variant="contained"
              startIcon={<PeopleIcon />}
              onClick={() => navigate('/user-management')}
              sx={{ mt: 2 }}
            >
              Manage Users
            </EasyCheckButton>
          </CardContent>
        </SettingsCard>
      )}

      {/* Security Section */}
      <SettingsCard>
        <CardContent sx={{ p: 3 }}>
          <SectionHeader>
            <LockIcon sx={{ color: 'warning.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Security Settings
            </Typography>
          </SectionHeader>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manage your account security and password settings.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <ChangePasswordForm 
            username={getUserEmail()} 
            onSuccess={() => {
              // Password changed successfully - form handles its own success state
            }}
          />
        </CardContent>
      </SettingsCard>
    </SettingsContainer>
  );
};

export default SettingsPage; 